import React from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Copy, Key, ExternalLink } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import apiSpec from '@/data/api-spec.json'

export function ApiDocs() {
  const { toast } = useToast()

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast({
      title: 'Kopiert!',
      description: 'In die Zwischenablage kopiert'
    })
  }

  const apiBaseUrl = window.location.origin + '/api'
  const exampleApiKey = 'YOUR_API_KEY_HERE'

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-4 text-foreground">API Dokumentation</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            RESTful API für Knowledge Management und Import Sources
          </p>
        </div>

        {/* Authentication Info */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Authentifizierung
            </CardTitle>
            <CardDescription>
              Alle API-Endpunkte erfordern eine API-Key Authentifizierung
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Header Format:</h3>
              <div className="bg-muted p-3 rounded-lg font-mono text-sm flex items-center justify-between">
                <span>x-api-key: {exampleApiKey}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(`x-api-key: ${exampleApiKey}`)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Base URL:</h3>
              <div className="bg-muted p-3 rounded-lg font-mono text-sm flex items-center justify-between">
                <span>{apiBaseUrl}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(apiBaseUrl)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Start Examples */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Quick Start Beispiele</CardTitle>
            <CardDescription>
              Beispiel-API-Aufrufe zum schnellen Einstieg
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">1. Knowledge abrufen:</h3>
              <div className="bg-muted p-3 rounded-lg">
                <code className="text-sm">
                  curl -H "x-api-key: YOUR_API_KEY" \<br />
                  &nbsp;&nbsp;{apiBaseUrl}/knowledge?limit=10&status=published
                </code>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">2. Import Source erstellen:</h3>
              <div className="bg-muted p-3 rounded-lg">
                <code className="text-sm">
                  curl -X POST \<br />
                  &nbsp;&nbsp;-H "x-api-key: YOUR_API_KEY" \<br />
                  &nbsp;&nbsp;-H "Content-Type: application/json" \<br />
                  &nbsp;&nbsp;-d '{`{"name":"Test Source","source_type":"manual","source_url":"https://example.com"}`}' \<br />
                  &nbsp;&nbsp;{apiBaseUrl}/import-sources
                </code>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Swagger UI - Iframe Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Interaktive API Dokumentation
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open('/api-docs.html', '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                In neuem Tab öffnen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const blob = new Blob([JSON.stringify(apiSpec, null, 2)], { type: 'application/json' })
                  const url = URL.createObjectURL(blob)
                  window.open(url, '_blank')
                  setTimeout(() => URL.revokeObjectURL(url), 100)
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                OpenAPI Spec
              </Button>
            </CardTitle>
            <CardDescription>
              Erkunden und testen Sie die API-Endpunkte interaktiv
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="relative">
              <iframe
                src="/api-docs.html"
                className="w-full h-[800px] border-0 rounded-lg bg-white"
                title="API Documentation"
                loading="lazy"
              />
              <div className="absolute top-4 right-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('/api-docs.html', '_blank')}
                  className="bg-white/95 backdrop-blur-sm text-gray-900 border-gray-300 hover:bg-white hover:text-gray-900"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="sr-only">Vollbild</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}